import React, { useState, useEffect, Dispatch, SetStateAction, useCallback } from "react";
import "./style.scss";

interface IExpiryCountdown {
  date: string;
  timerExpired: boolean;
  setTimerExpired: Dispatch<SetStateAction<boolean>>;
  setData: Dispatch<SetStateAction<any>>;
}

const calculateTimeRemaining = (dateString: string, utcTime: Date | null) => {
  const targetTime = new Date(dateString);

  if (!utcTime) {
    return null;
  }

  const timeDifference = (targetTime.getTime() + 300 * 60 * 1000) - utcTime.getTime();
  if (timeDifference <= 0) {
    return 0;
  } else return timeDifference;
};

const ExpiryCountdown: React.FC<IExpiryCountdown> = ({ date, timerExpired, setTimerExpired, setData }) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [utcTime, setUtcTime] = useState<Date | null>(null);

  const fetchCurrentUTCTime = useCallback(async () => {
    try {
      const response = await fetch('https://worldtimeapi.org/api/timezone/Etc/UTC');
      if (!response.ok) {
        throw new Error('First API failed');
      }
      const data = await response.json();
      setUtcTime(new Date(data.utc_datetime));
    } catch (error) {
      console.error('API failed', error);
      const dateTime = new Date();
      const utcDateTime = new Date(
        Date.UTC(
          dateTime.getUTCFullYear(),
          dateTime.getUTCMonth(),
          dateTime.getUTCDate(),
          dateTime.getUTCHours(),
          dateTime.getUTCMinutes(),
          dateTime.getUTCSeconds(),
          dateTime.getUTCMilliseconds()
        )
      );
      setUtcTime(utcDateTime);
    }
  }, []);
  
  useEffect(() => {
    fetchCurrentUTCTime();
  }, [fetchCurrentUTCTime]);

  useEffect(() => {
    if (utcTime) {
      const interval = setInterval(() => {
        setUtcTime(prevUtcTime => {
          if (prevUtcTime) {
            const newUtcTime = new Date(prevUtcTime.getTime() + 1000);
            setTimeRemaining(calculateTimeRemaining(date, newUtcTime));
            return newUtcTime;
          }
          return prevUtcTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [date, utcTime]);

  const formatTime = (time: number | null) => {
    if (time === null) return '--:--:--';

    if (time <= 0 && !timerExpired) {
      setTimerExpired(true);
      setData((prevData: any) => ({
        ...prevData,
        quotationDetails: {
          ...prevData?.quotationDetails,
          status: "EXPIRED",
        },
      }));
      return "00:00:00";
    } else if (time > 0) {
      setTimerExpired(false);
      const hours = Math.floor(time / (1000 * 60 * 60));
      const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((time % (1000 * 60)) / 1000);

      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    } else return "00:00:00";
  };

  return (
    <div className="expiry-timer-wrapper">
      {formatTime(timeRemaining) !== "00:00:00" && (
        <p>
          This quotation will expire in <span>{formatTime(timeRemaining)}</span>⌛
        </p>
      )}
      {formatTime(timeRemaining) === "00:00:00" && (
        <p>
          This quotation has expired <span>{formatTime(timeRemaining)}</span>⌛
        </p>
      )}
    </div>
  );
};

export default ExpiryCountdown;
